/**
 * US States mapping: Full name to abbreviation
 * Used for IDComply integration which requires state abbreviations (CA, OH, etc.)
 */

export const US_STATES = [
  { name: 'Alabama', abbreviation: 'AL' },
  { name: 'Alaska', abbreviation: 'AK' },
  { name: 'Arizona', abbreviation: 'AZ' },
  { name: 'Arkansas', abbreviation: 'AR' },
  { name: 'California', abbreviation: 'CA' },
  { name: 'Colorado', abbreviation: 'CO' },
  { name: 'Connecticut', abbreviation: 'CT' },
  { name: 'Delaware', abbreviation: 'DE' },
  { name: 'District of Columbia', abbreviation: 'DC' },
  { name: 'Florida', abbreviation: 'FL' },
  { name: 'Georgia', abbreviation: 'GA' },
  { name: 'Hawaii', abbreviation: 'HI' },
  { name: 'Idaho', abbreviation: 'ID' },
  { name: 'Illinois', abbreviation: 'IL' },
  { name: 'Indiana', abbreviation: 'IN' },
  { name: 'Iowa', abbreviation: 'IA' },
  { name: 'Kansas', abbreviation: 'KS' },
  { name: 'Kentucky', abbreviation: 'KY' },
  { name: 'Louisiana', abbreviation: 'LA' },
  { name: 'Maine', abbreviation: 'ME' },
  { name: 'Maryland', abbreviation: 'MD' },
  { name: 'Massachusetts', abbreviation: 'MA' },
  { name: 'Michigan', abbreviation: 'MI' },
  { name: 'Minnesota', abbreviation: 'MN' },
  { name: 'Mississippi', abbreviation: 'MS' },
  { name: 'Missouri', abbreviation: 'MO' },
  { name: 'Montana', abbreviation: 'MT' },
  { name: 'Nebraska', abbreviation: 'NE' },
  { name: 'Nevada', abbreviation: 'NV' },
  { name: 'New Hampshire', abbreviation: 'NH' },
  { name: 'New Jersey', abbreviation: 'NJ' },
  { name: 'New Mexico', abbreviation: 'NM' },
  { name: 'New York', abbreviation: 'NY' },
  { name: 'North Carolina', abbreviation: 'NC' },
  { name: 'North Dakota', abbreviation: 'ND' },
  { name: 'Ohio', abbreviation: 'OH' },
  { name: 'Oklahoma', abbreviation: 'OK' },
  { name: 'Oregon', abbreviation: 'OR' },
  { name: 'Pennsylvania', abbreviation: 'PA' },
  { name: 'Rhode Island', abbreviation: 'RI' },
  { name: 'South Carolina', abbreviation: 'SC' },
  { name: 'South Dakota', abbreviation: 'SD' },
  { name: 'Tennessee', abbreviation: 'TN' },
  { name: 'Texas', abbreviation: 'TX' },
  { name: 'Utah', abbreviation: 'UT' },
  { name: 'Vermont', abbreviation: 'VT' },
  { name: 'Virginia', abbreviation: 'VA' },
  { name: 'Washington', abbreviation: 'WA' },
  { name: 'West Virginia', abbreviation: 'WV' },
  { name: 'Wisconsin', abbreviation: 'WI' },
  { name: 'Wyoming', abbreviation: 'WY' },
] as const;

/**
 * Convert state name to abbreviation
 * @param stateName - Full state name (e.g., "California") or abbreviation (e.g., "CA")
 * @returns State abbreviation (e.g., "CA") or the original value if not found
 */
export function getStateAbbreviation(stateName: string): string {
  if (!stateName) return stateName;
  
  // Trim whitespace
  const trimmed = stateName.trim();
  
  // If already an abbreviation (2 letters), return as-is in uppercase
  if (trimmed.length === 2 && /^[A-Z]{2}$/i.test(trimmed)) {
    return trimmed.toUpperCase();
  }
  
  // Find state by name (case-insensitive, trimmed)
  const state = US_STATES.find(
    s => s.name.toLowerCase() === trimmed.toLowerCase()
  );
  
  if (state) {
    return state.abbreviation;
  }
  
  // If not found, return original (trimmed, uppercase if 2 chars)
  console.warn(`State not found in mapping: "${stateName}" (trimmed: "${trimmed}")`);
  return trimmed.length === 2 ? trimmed.toUpperCase() : trimmed;
}

/**
 * Get state name from abbreviation
 * @param abbreviation - State abbreviation (e.g., "CA")
 * @returns Full state name (e.g., "California") or the original value if not found
 */
export function getStateName(abbreviation: string): string {
  if (!abbreviation) return abbreviation;
  
  const state = US_STATES.find(
    s => s.abbreviation.toUpperCase() === abbreviation.toUpperCase()
  );
  
  return state ? state.name : abbreviation;
}

/**
 * Get all state names as an array
 */
export function getAllStateNames(): string[] {
  return US_STATES.map(s => s.name);
}

/**
 * Get all state abbreviations as an array
 */
export function getAllStateAbbreviations(): string[] {
  return US_STATES.map(s => s.abbreviation);
}

